import {
  ArrowDownRight,
  ArrowUpRight,
  Globe2,
  GripVertical,
  MousePointer2,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import {
  AccordionGallery,
  DepthText,
  PixelSwap,
  ScrollExpand,
} from "./ReactBits";
import { EditableImage, EditableLink, EditableText } from "./Editable";
import { itemsOf } from "../lib";
import { useEditor } from "../store";
import type { Item, Section } from "../types";
import { useCountUp } from "../hooks";

const iconMap = { MousePointer2, Zap, ShieldCheck, Globe2, Sparkles };

function Heading({
  section,
  intro = true,
}: {
  section: Section;
  intro?: boolean;
}) {
  return (
    <header className={`section-heading align-${section.style.align}`}>
      <EditableText
        section={section}
        field="kicker"
        as="span"
        className="kicker"
      />
      <EditableText section={section} field="title" as="h2" />
      {intro && section.content.intro != null && (
        <EditableText section={section} field="intro" as="p" />
      )}
    </header>
  );
}

export function Hero({ section }: { section: Section }) {
  const { editing, setEditing } = useEditor();
  return (
    <div className="hero-shell">
      <div className="hero-topline">
        <EditableText section={section} field="eyebrow" as="span" />
        <span>
          SCROLL TO EXPLORE <ArrowDownRight size={14} />
        </span>
      </div>
      <div className="hero-grid">
        <div className="hero-title-wrap">
          <DepthText
            text={String(section.content.title)}
            layers={Number(section.content.layers)}
            depth={Number(section.content.depth)}
            faceColor={String(section.content.faceColor)}
            depthColor={String(section.content.depthColor)}
            tilt={Number(section.content.tilt)}
            perspective={Number(section.content.perspective)}
            pointerTracking={Boolean(section.content.pointerTracking)}
            smoothing={Number(section.content.smoothing)}
            autoOrbit={Boolean(section.content.orbit)}
            orbitSpeed={Number(section.content.orbitSpeed)}
            fontSize={Number(section.content.fontSize)}
            fontWeight={Number(section.content.fontWeight)}
            shadow={Boolean(section.content.shadow)}
          />
          {editing && (
            <button
              className="depth-edit-hit"
              onClick={() =>
                useEditor.getState().select({
                  sectionId: section.id,
                  kind: "text",
                  field: "title",
                })
              }
              aria-label="Edit hero title"
            />
          )}
        </div>
        <div className="hero-copy">
          <EditableText section={section} field="intro" as="p" />
          <EditableLink
            section={section}
            field="button"
            className="circle-link"
          >
            <EditableText section={section} field="button" as="span" />
            <ArrowUpRight />
          </EditableLink>
        </div>
        <EditableImage
          section={section}
          className="hero-image"
          loading="eager"
        />
        <div className="hero-note">
          <MousePointer2 size={18} />
          <span>Try it yourself</span>
          <button onClick={() => setEditing(!editing)}>
            {editing ? "View the site" : "Turn on Edit Site"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Services({ section }: { section: Section }) {
  const { editing, reorderItems, select, selection } = useEditor();
  const items = itemsOf(section);
  return (
    <div className="container">
      <Heading section={section} />
      <div
        className="service-grid"
        style={{
          gridTemplateColumns: `repeat(${section.style.columns}, minmax(0,1fr))`,
          gap: section.style.gap,
        }}
      >
        {items.map((item) => {
          const Icon = iconMap[item.icon as keyof typeof iconMap] ?? Sparkles;
          return (
            <article
              key={item.id}
              className={selection?.itemId === item.id ? "cms-selected" : ""}
              draggable={editing}
              onDragStart={(e) => e.dataTransfer.setData("text/plain", item.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) =>
                reorderItems(
                  section.id,
                  e.dataTransfer.getData("text/plain"),
                  item.id,
                )
              }
              onClick={() =>
                editing &&
                select({ sectionId: section.id, kind: "item", itemId: item.id })
              }
            >
              {editing && <GripVertical className="item-grip" size={16} />}
              <div className="service-icon">
                <Icon />
              </div>
              <EditableText
                section={section}
                item={item}
                field="label"
                as="span"
                className="service-no"
              />
              <EditableText
                section={section}
                item={item}
                field="title"
                as="h3"
              />
              <EditableText section={section} item={item} field="text" as="p" />
            </article>
          );
        })}
      </div>
    </div>
  );
}

export function PixelSwapSection({ section }: { section: Section }) {
  return (
    <div className="container pixel-layout">
      <Heading section={section} intro={false} />
      <PixelSwap
        firstContent={
          <div className="pixel-state pixel-state-before">
            <span>01 / OLD WAY</span>
            {String(section.content.before)
              .split("\n")
              .map((line, index) =>
                index === 0 ? (
                  <strong key={line}>{line}</strong>
                ) : (
                  <p key={line}>{line}</p>
                ),
              )}
          </div>
        }
        secondContent={
          <div className="pixel-state pixel-state-after">
            <span>02 / ZHSTUDIO</span>
            {String(section.content.after)
              .split("\n")
              .map((line, index) =>
                index === 0 ? (
                  <strong key={line}>{line}</strong>
                ) : (
                  <p key={line}>{line}</p>
                ),
              )}
          </div>
        }
        pixelSize={Number(section.content.pixelSize)}
        gap={Number(section.content.gap)}
        pixelRadius={Number(section.content.pixelRadius)}
        pixelSpin={Number(section.content.spin)}
        pixelScale={Number(section.content.scale)}
        duration={Number(section.content.duration)}
        pixelDuration={Number(section.content.pixelDuration)}
        pattern={String(section.content.pattern) as "diagonal"}
        randomness={Number(section.content.randomness)}
        fade={Boolean(section.content.fade)}
        trigger={String(section.content.trigger)}
      />
    </div>
  );
}

export function Gallery({ section }: { section: Section }) {
  const { select, selection, reorderItems } = useEditor();
  return (
    <div className="container wide">
      <Heading section={section} />
      <AccordionGallery
        items={itemsOf(section)}
        grayscale={Boolean(section.content.grayscale)}
        parallax={Number(section.content.parallax)}
        tilt={Number(section.content.tilt)}
        gap={section.style.gap}
        radius={section.style.radius}
        expandRatio={Number(section.content.expandRatio)}
        orientation={String(section.content.orientation)}
        duration={Number(section.content.timing)}
        trigger={String(section.content.trigger)}
        selectedId={selection?.itemId}
        onSelect={(itemId) =>
          select({ sectionId: section.id, kind: "item", itemId })
        }
        onReorder={(a, o) => reorderItems(section.id, a, o)}
      />
    </div>
  );
}

function Stat({ section, item }: { section: Section; item: Item }) {
  const ref = useCountUp(item.value ?? "0");
  return (
    <article>
      <span className="stat-value" ref={ref}>
        {item.value}
      </span>
      <EditableText section={section} item={item} field="title" as="h3" />
      <EditableText section={section} item={item} field="text" as="p" />
    </article>
  );
}
export function Stats({ section }: { section: Section }) {
  return (
    <div className="container">
      <Heading section={section} intro={false} />
      <div className="stats-grid">
        {itemsOf(section).map((item) => (
          <Stat key={item.id} section={section} item={item} />
        ))}
      </div>
    </div>
  );
}

export function ScrollExpandSection({ section }: { section: Section }) {
  return (
    <ScrollExpand
      image={String(section.content.image)}
      alt={String(section.content.alt)}
      title={String(section.content.title)}
      hint={String(section.content.hint)}
      startWidth={Number(section.content.startWidth)}
      startHeight={Number(section.content.startHeight)}
      startRadius={Number(section.content.startRadius)}
      endRadius={Number(section.content.endRadius)}
      mediaZoom={Number(section.content.mediaZoom)}
      scrollDistance={Number(section.content.scrollDistance)}
      smoothing={Number(section.content.smoothing)}
      overlay={Number(section.content.overlay)}
    />
  );
}

export function Testimonials({ section }: { section: Section }) {
  return (
    <div className="container">
      <Heading section={section} intro={false} />
      <div className="quote-grid">
        {itemsOf(section).map((item, index) => (
          <figure key={item.id}>
            <span>0{index + 1} / 03</span>
            <EditableText
              section={section}
              item={item}
              field="title"
              as="blockquote"
            />
            <EditableText
              section={section}
              item={item}
              field="text"
              as="figcaption"
            />
          </figure>
        ))}
      </div>
    </div>
  );
}

const dayIndex = (new Date().getDay() + 6) % 7;
export function Hours({ section }: { section: Section }) {
  const { editing, select, selection } = useEditor();
  const items = itemsOf(section);
  const today = items[dayIndex];
  const hour = new Date().getHours() + new Date().getMinutes() / 60;
  const open =
    today?.value === "open" &&
    hour >= Number(today.title?.split(":")[0]) &&
    hour < Number(today.text?.split(":")[0]);
  return (
    <div className="container hours-layout">
      <div>
        <Heading section={section} />
        <div className={`open-status ${open ? "open" : ""}`}>
          <i />
          {open ? `Open now · until ${today?.text}` : "Currently closed"}
        </div>
      </div>
      <div className="hours-list">
        {items.map((item, index) => (
          <button
            key={item.id}
            onClick={() =>
              editing &&
              select({ sectionId: section.id, kind: "item", itemId: item.id })
            }
            className={`${selection?.itemId === item.id ? "cms-selected" : ""} ${index === dayIndex ? "today" : ""}`}
          >
            <span>
              {item.label}
              {index === dayIndex && <small>TODAY</small>}
            </span>
            <strong>
              {item.value === "closed"
                ? "Closed"
                : `${item.title} — ${item.text}`}
            </strong>
            {item.category && <em>{item.category}</em>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Blog({ section }: { section: Section }) {
  const items = itemsOf(section);
  return (
    <div className="container">
      <Heading section={section} intro={false} />
      <div className={`blog-grid layout-${section.layout}`}>
        {items.map((item, index) => (
          <article key={item.id} className={index === 0 ? "featured" : ""}>
            <EditableImage section={section} item={item} />
            <div>
              <span className="post-meta">
                <EditableText
                  section={section}
                  item={item}
                  field="category"
                  as="b"
                />{" "}
                ·{" "}
                <EditableText
                  section={section}
                  item={item}
                  field="date"
                  as="time"
                />
              </span>
              <EditableText
                section={section}
                item={item}
                field="title"
                as="h3"
              />
              <EditableText section={section} item={item} field="text" as="p" />
              <a href="#contact" aria-label={`Read ${item.title}`}>
                <ArrowUpRight />
              </a>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export function FAQ({ section }: { section: Section }) {
  return (
    <div className="container faq-layout">
      <Heading section={section} intro={false} />
      <div className="faq-list">
        {itemsOf(section).map((item, index) => (
          <details key={item.id} open={index === 0}>
            <summary>
              <EditableText
                section={section}
                item={item}
                field="title"
                as="span"
              />
              <b>+</b>
            </summary>
            <EditableText section={section} item={item} field="text" as="p" />
          </details>
        ))}
      </div>
    </div>
  );
}

export function CTA({ section }: { section: Section }) {
  const setEditing = useEditor((s) => s.setEditing);
  return (
    <div className="container cta-inner">
      <EditableText
        section={section}
        field="kicker"
        as="span"
        className="kicker"
      />
      <EditableText section={section} field="title" as="h2" />
      <EditableText section={section} field="intro" as="p" />
      <div>
        <button className="button dark" onClick={() => setEditing(true)}>
          <EditableText section={section} field="button" as="span" />
          <ArrowUpRight />
        </button>
        <a className="text-button" href="mailto:hello@zhstudio.ch">
          <EditableText section={section} field="secondary" as="span" />
        </a>
      </div>
    </div>
  );
}

export function Content({ section }: { section: Section }) {
  return (
    <div className={`container content-block ${section.layout}`}>
      <div>
        <Heading section={section} />
        {Boolean(section.content.showButton) && (
          <EditableLink
            section={section}
            field="button"
            className="button dark"
          >
            <EditableText section={section} field="button" as="span" />
            <ArrowUpRight />
          </EditableLink>
        )}
      </div>
      {Boolean(section.content.showImage) && (
        <EditableImage section={section} className="content-image" />
      )}
    </div>
  );
}

export const SECTION_REGISTRY = {
  hero: Hero,
  content: Content,
  services: Services,
  "pixel-swap": PixelSwapSection,
  gallery: Gallery,
  stats: Stats,
  "scroll-expand": ScrollExpandSection,
  testimonials: Testimonials,
  hours: Hours,
  blog: Blog,
  faq: FAQ,
  cta: CTA,
};
